/**
 * Offline PIN sign-in (06-flows.md F10 step 1; docs/dev.md "Offline PIN sign-in").
 *
 * A signed-in till downloads the outlet's PIN verifiers (`GET {o}/staff/offline-pins`:
 * argon2id PHC strings with their own salt, never the server's PIN hashes, and none for people
 * who can approve overrides, whose PIN never leaves the server). They are kept sealed with
 * AES-GCM under a key derived from this device's secret, in IndexedDB, and checked with an
 * argon2id implementation (WASM) while offline. The cache is refreshed whenever a till is
 * signed in and wiped when the server says this terminal is blocked or gone.
 *
 * Decisions on the residual risks listed in docs/dev.md:
 *  - verifiers are wrapped with a per-device key (the terminal secret), so a copy of the
 *    IndexedDB database alone is useless;
 *  - five wrong PINs in a row lock that person's offline sign-in for five minutes on this
 *    device (the server's own lockout cannot count offline tries);
 *  - what an offline session may do is bounded by the `pos` permissions in the bundle, and
 *    the server re-checks every command when it syncs.
 */
import { isApiError, unwrap } from '@qrkey/api-client';
import { api } from '../app/api.js';
import { terminalStore } from '../app/terminal.js';
import { db, kvGet, kvSet, type PinCache } from './db.js';
import type { OfflinePerson } from './session.js';

interface SealedStaff {
  userId: string;
  name: string;
  roleId: string;
  roleName: string;
  offlineVerifier: string | null;
  permissions: string[];
}

export interface RosterEntry {
  userId: string;
  name: string;
  roleName: string;
  canPin: boolean;
}

const MAX_TRIES = 5;
const LOCK_MS = 5 * 60_000;

const enc = new TextEncoder();
const dec = new TextDecoder();
const b64 = (bytes: Uint8Array) => btoa(String.fromCharCode(...bytes));
const unb64 = (text: string) => Uint8Array.from(atob(text), (c) => c.charCodeAt(0));

async function deviceKey(secret: string): Promise<CryptoKey> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    enc.encode(`qrkey-offline-pins:v1:${secret}`),
  );
  return crypto.subtle.importKey('raw', digest, 'AES-GCM', false, ['encrypt', 'decrypt']);
}

async function seal(secret: string, staff: SealedStaff[]) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    await deviceKey(secret),
    enc.encode(JSON.stringify(staff)),
  );
  return { iv: b64(iv), sealed: b64(new Uint8Array(cipher)) };
}

async function unseal(secret: string, cache: PinCache): Promise<SealedStaff[] | null> {
  try {
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: unb64(cache.iv) },
      await deviceKey(secret),
      unb64(cache.sealed),
    );
    return JSON.parse(dec.decode(plain)) as SealedStaff[];
  } catch {
    return null;
  }
}

/** Download and keep the outlet's offline PIN material. Needs a signed-in till session. */
export async function refreshOfflinePins(outletId: string): Promise<boolean> {
  const terminal = terminalStore.get();
  if (!terminal || terminal.outletId !== outletId) return false;
  try {
    const bundle = await unwrap(
      api.client.GET('/v1/outlets/{outletId}/staff/offline-pins', {
        params: { path: { outletId } },
      }),
    );
    const staff: SealedStaff[] = bundle.staff.map((s) => ({
      userId: s.userId,
      name: s.name,
      roleId: s.roleId,
      roleName: s.roleName,
      offlineVerifier: s.offlineVerifier,
      permissions: [...s.permissions],
    }));
    const { iv, sealed } = await seal(terminal.secret, staff);
    // The checker is loaded now, while the cloud answers: offline there is nothing to fetch it from.
    void import('hash-wasm').catch(() => undefined);
    await db().pins.put({
      outletId,
      terminalId: terminal.terminalId,
      generatedAt: bundle.generatedAt,
      iv,
      sealed,
      roster: staff.map((s) => ({
        userId: s.userId,
        name: s.name,
        roleName: s.roleName,
        canPin: s.offlineVerifier !== null,
      })),
    });
    return true;
  } catch (error) {
    // The terminal was blocked or left the outlet: nothing may stay on this device.
    if (isApiError(error, 'TERMINAL_BLOCKED') || isApiError(error, 'TERMINAL_NOT_REGISTERED')) {
      await wipeOfflinePins();
    }
    return false;
  }
}

export async function wipeOfflinePins(): Promise<void> {
  await db().pins.clear();
}

/** Who can sign in here without the cloud, for the PIN screen. */
export async function offlineRoster(outletId: string): Promise<RosterEntry[]> {
  const cache = await db().pins.get(outletId);
  return cache ? cache.roster.filter((r) => r.canPin) : [];
}

interface Tries {
  fails: number;
  lockedUntil: number | null;
}

const triesKey = (userId: string) => `pin-tries:${userId}`;

/** When this person's offline sign-in unlocks (ms since epoch), or null. */
export async function lockedUntil(userId: string): Promise<number | null> {
  const t = await kvGet<Tries>(triesKey(userId));
  return t?.lockedUntil && t.lockedUntil > Date.now() ? t.lockedUntil : null;
}

export type OfflinePinResult =
  | { ok: true; person: OfflinePerson }
  | { ok: false; reason: 'wrong' | 'locked' | 'unavailable'; lockedUntil?: number };

/** Check a PIN against the cached verifier. */
export async function verifyOfflinePin(
  outletId: string,
  userId: string,
  pin: string,
): Promise<OfflinePinResult> {
  const terminal = terminalStore.get();
  const cache = await db().pins.get(outletId);
  if (!terminal || !cache) return { ok: false, reason: 'unavailable' };
  const until = await lockedUntil(userId);
  if (until) return { ok: false, reason: 'locked', lockedUntil: until };
  const staff = (await unseal(terminal.secret, cache))?.find((s) => s.userId === userId);
  if (!staff?.offlineVerifier) return { ok: false, reason: 'unavailable' };
  let good = false;
  try {
    // Loaded when first needed: the WASM is not part of the app's first load.
    const { argon2Verify } = await import('hash-wasm');
    good = await argon2Verify({ password: pin, hash: staff.offlineVerifier });
  } catch {
    good = false;
  }
  if (!good) {
    const before = (await kvGet<Tries>(triesKey(userId))) ?? { fails: 0, lockedUntil: null };
    const fails = before.fails + 1;
    const locked = fails >= MAX_TRIES;
    await kvSet(triesKey(userId), {
      fails: locked ? 0 : fails,
      lockedUntil: locked ? Date.now() + LOCK_MS : null,
    });
    return locked
      ? { ok: false, reason: 'locked', lockedUntil: Date.now() + LOCK_MS }
      : { ok: false, reason: 'wrong' };
  }
  await kvSet(triesKey(userId), { fails: 0, lockedUntil: null });
  return {
    ok: true,
    person: {
      userId: staff.userId,
      name: staff.name,
      roleId: staff.roleId,
      roleName: staff.roleName,
      permissions: staff.permissions,
    },
  };
}
